import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, Injectable, Injector, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app/app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SignUpComponent } from './auth/sign-up/sign-up.component';
import { LandingComponent } from './landing/landing.component';
import { PageNotFoundComponent } from './common/page-not-found/page-not-found.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { InjectHelper } from './helper/inject.helper';
import { ErrorDetailsComponent, NotificationDetailsComponent } from './helper/notification.handler';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BasicAuthInterceptor } from './helper/basic-auth.interceptor';
import { ErrorInterceptor } from './helper/error.interceptor';
import { environment } from '../environments/environment';
import { Auth } from '@aws-amplify/auth';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MainContentHolderComponent } from './common/main-content-holder/main-content-holder.component';
import { FormInputComponent } from './common/form-input/form-input.component';
import { ButtonComponent } from './common/button/button.component';
import { SocialSignInComponent } from './auth/social-sign-in/social-sign-in.component';
import { LinkComponent } from './common/link/link.component';
import { SignUpConfirmComponent } from './auth/sign-up-confirm/sign-up-confirm.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LogoComponent } from './common/logo/logo.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { AvatarComponent } from './common/avatar/avatar.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContestsAvailableListComponent } from './contest/contests-available-list/contests-available-list.component';
import { TeachersListComponent } from './teacher/teachers-list/teachers-list.component';
import { StudentsListComponent } from './student/students-list/students-list.component';
import { NoContentComponent } from './common/no-content/no-content.component';
import { CompetitionsListComponent } from './competition/competitions-list/competitions-list.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { PermissionCheckerComponent } from './common/permission-checker/permission-checker.component';
import { MatMenuModule } from '@angular/material/menu';
import { ProgressService } from './service/progress.service';
import { DynamicOverlay } from './common/overlay/dynamic-overlay.service';
import { DynamicOverlayContainer } from './common/overlay/dynamic-overlay-container';
import { ProgressContainerComponent } from './common/progress-container/progress-container.component';
import { ListItemDetailsModalComponent } from './common/list-item-details-modal/list-item-details-modal.component';
import { MatDialogModule } from '@angular/material/dialog';
import {
  CompetitionDetailsModalComponent
} from './competition/competition-details-modal/competition-details-modal.component';
import { LanguageSwitchComponent } from './common/language-switch/language-switch.component';
import { ConfirmationModalComponent } from './common/modal/confirmation-modal/confirmation-modal.component';
import {
  DeleteConfirmationModalComponent
} from './common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import { HostsListComponent } from './host/hosts-list/hosts-list.component';
import { MobileAwareComponent } from './common/mobile-aware/mobile-aware.component';
import { CompetitionSelectComponent } from './competition/competition-select/competition-select.component';
import { MatSelectModule } from '@angular/material/select';
import { InviteUserModalComponent } from './profile/invite-user-modal/invite-user-modal.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ProfileDetailsComponent } from './profile/profile-details/profile-details.component';
import { ProfileComponent } from './profile/profile/profile.component';
import { MatTabsModule } from '@angular/material/tabs';
import { ProfileDetailsModalComponent } from './profile/profile-details-modal/profile-details-modal.component';
import { RoleSelectComponent } from './auth/role-select/role-select.component';
import { CompetitionDetailsComponent } from './competition/competition-details/competition-details.component';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { EditorModule } from '@tinymce/tinymce-angular';
import { ContestDetailsModalComponent } from './contest/contest-details-modal/contest-details-modal.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ItemSelectComponent } from './common/item-select/item-select.component';
import { SchoolSelectComponent } from './profile/school-select/school-select.component';
import { ItemAutocompleteComponent } from './common/item-autocomplete/item-autocomplete.component';
import { SchoolDetailsComponent } from './profile/school-details/school-details.component';
import { ProfileSchoolDetailsComponent } from './profile/profile-school-details/profile-school-details.component';
import { HelpComponent } from './support/help/help.component';
import { PassResetComponent } from './auth/pass-reset/pass-reset.component';
import { PassResetConfirmComponent } from './auth/pass-reset-confirm/pass-reset-confirm.component';
import { ContestsListComponent } from './contest/contests-list/contests-list.component';
import { ContestsHistoryListComponent } from './contest/contests-history-list/contests-history-list.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { ContestEnrolModalComponent } from './contest/contest-enrol-modal/contest-enrol-modal.component';
import { ContestSelectComponent } from './contest/contest-select/contest-select.component';
import { SchoolModifyDetailsComponent } from './profile/school-modify-details/school-modify-details.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ServiceTermsComponent } from './support/service-terms/service-terms.component';
import { ServicePrivacyComponent } from './support/service-privacy/service-privacy.component';
import { ContestPaymentConfirmComponent } from './contest/contest-payment-confirm/contest-payment-confirm.component';
import { ContestPaymentModalComponent } from './contest/contest-payment-modal/contest-payment-modal.component';
import { ContestEnrollmentAwareComponent } from './contest/contest-enrollment-aware/contest-enrollment-aware.component';
import { ContestPaymentRedirectComponent } from './contest/contest-payment-redirect/contest-payment-redirect.component';
import {
  ContestPaymentConfirmModalComponent
} from './contest/contest-payment-confirm-modal/contest-payment-confirm-modal.component';
import { TenantService } from './service/tenant.service';
import { CoordinatorsListComponent } from './coordinator/coordinators-list/coordinators-list.component';
import { SchoolDetailsModalComponent } from './profile/school-details-modal/school-details-modal.component';
import { ContestSelectPanelComponent } from './contest/contest-select-panel/contest-select-panel.component';
import { EnrolStatsComponent } from './stats/enrol/enrol-stats/enrol-stats.component';
import { RegionEnrolStatsComponent } from './stats/enrol/region-enrol-stats/region-enrol-stats.component';
import { SchoolEnrolStatsComponent } from './stats/enrol/school-enrol-stats/school-enrol-stats.component';
import { NgChartsModule } from 'ng2-charts';
import { MatTableModule } from '@angular/material/table';
import { MatBadgeModule } from '@angular/material/badge';
import { ContestsManageListComponent } from './contest/contests-manage-list/contests-manage-list.component';
import { ContestTasksEditorComponent } from './contest-task/contest-tasks-editor/contest-tasks-editor.component';
import { ContestSectionComponent } from './contest-task/contest-section/contest-section.component';
import { ContestQuestionComponent } from './contest-task/contest-question/contest-question.component';
import {
  ContestQuestionDetailsModalComponent
} from './contest-task/contest-question-details-modal/contest-question-details-modal.component';
import { MathModule } from './math/math.module';
import { MatRadioModule } from '@angular/material/radio';
import { DragDropDirective } from './common/file-upload/drag-drop.directive';
import { FileUploadComponent } from './common/file-upload/file-upload.component';
import { ContestExamCenterComponent } from './contest-exam/contest-exam-center/contest-exam-center.component';
import { TimerComponent } from './common/timer/timer.component';
import { CanDeactivateGuard } from './helper/can-deactivate.guard';
import { ClientTimeShiftComponent } from './common/modal/client-time-shift-modal/client-time-shift.component';
import { ExamCardStatsComponent } from './stats/exam-card/exam-card-stats/exam-card-stats.component';
import { AnswerSheetComponent } from './stats/exam-card/answer-sheet/answer-sheet.component';
import { ExamRatingStatsComponent } from './stats/exam/exam-rating-stats/exam-rating-stats.component';
import { StatsDashboardComponent } from './stats/stats-dasboard/stats-dashboard.component';
import { ExamResultsStatsComponent } from './stats/exam/exam-results/exam-results-stats.component';
import {
  ContestCertificateModalComponent
} from './contest/contest-certificate-modal/contest-certificate-modal.component';
import { TeacherResultsStatsComponent } from './stats/teacher/teacher-results/teacher-results-stats.component';
import { ExamResultsTableComponent } from './stats/exam/exam-results-table/exam-results-table.component';
import { ExamSchoolResultsStatsComponent } from './stats/exam/exam-school-results/exam-school-results-stats.component';
import { PrintableReportStatsComponent } from './stats/print/printable-report/printable-report-stats.component';
import { GradeResultsTableComponent } from './stats/exam/grade-results-table/grade-results-table.component';
import { TeacherInfoTableComponent } from './stats/teacher/teacher-info-table/teacher-info-table.component';
import { SchoolTeachersInfoComponent } from './stats/teacher/school-teachers-info/school-teachers-info.component';
import {
  ExamRegionSchoolsResultStatsComponent
} from './stats/exam/exam-region-schools-result/exam-region-schools-result-stats.component';
import {
  ExamRegionSchoolsResultTableComponent
} from './stats/exam/exam-region-schools-result-table/exam-region-schools-result-table.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ContestPreviewModalComponent } from './contest/contest-preview-modal/contest-preview-modal.component';
import { ImportUsersModalComponent } from './profile/import-users-modal/import-users-modal.component';
import { ContestDirectUrlModalComponent } from './contest/contest-direct-url-modal/contest-direct-url-modal.component';
import { MigrationsDashboardComponent } from './migration/migrations-dashboard/migrations-dashboard.component';
import * as Sentry from '@sentry/browser';
import { MediaPlaybackComponent } from './common/media-playback/media-playback.component';
import { CheckAllComponent } from './common/check-all/check-all.component';
import { StudentsPasswordsTableComponent } from './student/students-passwords-table/students-passwords-table.component';
import { ChangePassResultModalComponent } from './profile/change-pass-result-modal/change-pass-result-modal.component';
import { ExamProgressModalComponent } from './stats/exam/exam-progress-modal/exam-progress-modal.component';
import { SettingsDashboardComponent } from './settings/settings-dasboard/settings-dashboard.component';
import { SchoolsManageComponent } from './settings/school-manage/schools-manage.component';
import {
  SchoolModifyDetailsModalComponent
} from './settings/school-modify-details-modal/school-modify-details-modal.component';
import {
  ContestParticipationStatsComponent
} from './stats/contest/contest-participation-stats/contest-participation-stats.component';
import { DoughnutChartComponent } from './stats/common/doughnut-chart/doughnut-chart.component';
import { BarChartComponent } from './stats/common/bar-chart/bar-chart.component';
import { PolarChartComponent } from './stats/common/polar-chart/polar-chart.component';
import { UsersTableComponent } from './profile/users-table/users-table.component';
import { ListItemDetailsComponent } from './common/list-item-details/list-item-details.component';
import { SelectTeacherModalComponent } from './teacher/select-teacher-modal/select-teacher-modal.component';
import {
  ContestTaskCloneSelectModalComponent
} from './contest/contest-task-clone-select-modal/contest-task-clone-select-modal.component';
import {
  ContestsTeacherInfoModalComponent
} from './contest/contest-teacher-info-modal/contests-teacher-info-modal.component';

Sentry.init({
  enabled: environment.sentry.enabled,
  dsn: environment.sentry.dns,
  environment: environment.env,
  attachStacktrace: true,
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    Sentry.captureException(error.originalError || error);
  }
}

@NgModule({
  declarations: [
    AppComponent,
    PermissionCheckerComponent,
    LogoComponent,
    SignInComponent,
    SignUpComponent,
    SignUpConfirmComponent,
    RoleSelectComponent,
    PassResetComponent,
    PassResetConfirmComponent,
    LandingComponent,

    ClientTimeShiftComponent,

    MainContentHolderComponent,
    FormInputComponent,
    DragDropDirective,
    FileUploadComponent,
    ItemSelectComponent,
    ItemAutocompleteComponent,
    CheckAllComponent,

    NoContentComponent,
    PageNotFoundComponent,
    NotificationDetailsComponent,
    ErrorDetailsComponent,
    ButtonComponent,
    ProgressContainerComponent,
    ListItemDetailsComponent,
    ListItemDetailsModalComponent,
    ConfirmationModalComponent,
    DeleteConfirmationModalComponent,
    SocialSignInComponent,
    LinkComponent,
    AvatarComponent,
    ContestsListComponent,
    ContestSelectComponent,
    ContestSelectPanelComponent,
    ContestsManageListComponent,
    ContestsHistoryListComponent,
    ContestsAvailableListComponent,
    ContestDetailsModalComponent,
    ContestEnrollmentAwareComponent,
    ContestEnrolModalComponent,
    ContestPaymentModalComponent,
    ContestCertificateModalComponent,
    ContestPreviewModalComponent,
    ContestDirectUrlModalComponent,
    ContestTaskCloneSelectModalComponent,
    ContestsTeacherInfoModalComponent,
    CompetitionsListComponent,
    CompetitionDetailsModalComponent,
    CompetitionDetailsComponent,
    CompetitionSelectComponent,
    TeachersListComponent,
    SelectTeacherModalComponent,
    StudentsListComponent,
    StudentsPasswordsTableComponent,
    ChangePassResultModalComponent,
    LanguageSwitchComponent,
    HostsListComponent,
    InviteUserModalComponent,
    ImportUsersModalComponent,
    MobileAwareComponent,
    MediaPlaybackComponent,

    ProfileComponent,
    ProfileDetailsComponent,
    ProfileDetailsModalComponent,
    ProfileSchoolDetailsComponent,
    UsersTableComponent,
    SchoolSelectComponent,
    SchoolDetailsComponent,
    SchoolModifyDetailsComponent,
    SchoolDetailsModalComponent,
    HelpComponent,
    ContestPaymentConfirmComponent,
    ContestPaymentRedirectComponent,
    ContestPaymentConfirmModalComponent,
    ServiceTermsComponent,
    ServicePrivacyComponent,

    CoordinatorsListComponent,

    EnrolStatsComponent,
    RegionEnrolStatsComponent,
    SchoolEnrolStatsComponent,

    ExamCardStatsComponent,
    AnswerSheetComponent,
    StatsDashboardComponent,

    DoughnutChartComponent,
    BarChartComponent,
    PolarChartComponent,
    GradeResultsTableComponent,
    ExamProgressModalComponent,
    ExamResultsTableComponent,
    ExamResultsStatsComponent,
    ExamRatingStatsComponent,
    ExamSchoolResultsStatsComponent,
    ExamRegionSchoolsResultStatsComponent,
    ExamRegionSchoolsResultTableComponent,
    TeacherInfoTableComponent,
    SchoolTeachersInfoComponent,
    TeacherResultsStatsComponent,
    PrintableReportStatsComponent,
    ContestParticipationStatsComponent,

    ContestTasksEditorComponent,

    ContestSectionComponent,

    MigrationsDashboardComponent,

    ContestQuestionComponent,
    ContestQuestionDetailsModalComponent,
    ContestExamCenterComponent,
    TimerComponent,

    SettingsDashboardComponent,
    SchoolsManageComponent,
    SchoolModifyDetailsModalComponent,
  ],
  imports: [
    NgxWebstorageModule.forRoot(
      {
        prefix: `${environment.env}.${TenantService.getSubdomain() ? TenantService.getSubdomain() : 'root'}`,
        separator: '.',
        caseSensitive: true
      }
    ),

    HttpClientModule,

    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,

    FlexLayoutModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,

    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatToolbarModule,
    MatSidenavModule,
    MatTabsModule,
    MatListModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatCardModule,
    MatRippleModule,
    MatExpansionModule,
    MatSlideToggleModule,

    MatDatepickerModule,
    MatNativeDateModule,

    MatBadgeModule,

    MatTableModule,

    EditorModule,

    NgChartsModule,

    MathModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler
    },
    ProgressService,
    DynamicOverlay,
    DynamicOverlayContainer,
    CanDeactivateGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

  constructor(injector: Injector) {
    InjectHelper.setInjector(injector);

    Auth.configure({
      // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
      // identityPoolId: 'XX-XXXX-X:XXXXXXXX-XXXX-1234-abcd-1234567890ab',

      // REQUIRED - Amazon Cognito Region ('XX-XXXX-X')
      region: environment.auth.region,

      // OPTIONAL - Amazon Cognito Federated Identity Pool Region
      // Required only if it's different from Amazon Cognito Region
      // 'XX-XXXX-X'
      // identityPoolRegion: environment.auth.region,

      // OPTIONAL - Amazon Cognito User Pool ID ('XX-XXXX-X_abcd1234')
      userPoolId: environment.auth.poolId,

      // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
      userPoolWebClientId: environment.auth.clientId,

      // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
      mandatorySignIn: true,

      oauth: {
        domain: environment.oauth.domain,
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: environment.oauth.redirectSignIn,
        redirectSignOut: environment.oauth.redirectSignOut,

        // 'code' for Authorization code grant,
        // 'token' for Implicit grant
        responseType: 'code',
        options: {
          AdvancedSecurityDataCollectionFlag: false
        }
      }
    });
  }

}
